const defaultReconnect = {
    baseDelayMs: 600,
    maxDelayMs: 8000
};

export class RealtimeWsClient {
    constructor({ url, onCaption, onStatus, onError }) {
        this.url = url;
        this.onCaption = onCaption;
        this.onStatus = onStatus;
        this.onError = onError;
        this.socket = null;
        this.reconnectAttempts = 0;
        this.shouldReconnect = true;
        this.pendingConfig = null;
        this.reconnectTimer = null;
        this.reconnectConfig = defaultReconnect;
    }

    connect(config) {
        this.pendingConfig = config;
        this.shouldReconnect = true;
        this.#open();
    }

    #open() {
        if (this.socket) {
            this.socket.close();
        }

        this.onStatus?.({ state: "connecting" });
        const socket = new WebSocket(this.url);
        this.socket = socket;

        socket.binaryType = "arraybuffer";

        socket.addEventListener("open", () => {
            this.reconnectAttempts = 0;
            this.onStatus?.({ state: "connected" });
            if (this.pendingConfig) {
                socket.send(JSON.stringify({ type: "config", ...this.pendingConfig }));
            }
        });

        socket.addEventListener("message", (event) => {
            if (typeof event.data !== "string") {
                return;
            }

            let message;
            try {
                message = JSON.parse(event.data);
            } catch (error) {
                this.onError?.({ message: "Invalid server message" });
                return;
            }

            if (message.type === "caption") {
                this.onCaption?.(message);
            }

            if (message.type === "error") {
                this.onError?.(message);
            }

            if (message.type === "warning") {
                this.onError?.({ message: `Warning: ${message.message || "Unknown warning"}` });
            }
        });

        socket.addEventListener("close", () => {
            this.onStatus?.({ state: "disconnected" });
            if (this.shouldReconnect) {
                this.#scheduleReconnect();
            }
        });

        socket.addEventListener("error", () => {
            this.onError?.({ message: "WebSocket error" });
        });
    }

    #scheduleReconnect() {
        const { baseDelayMs, maxDelayMs } = this.reconnectConfig;
        const delay = Math.min(maxDelayMs, baseDelayMs * 2 ** this.reconnectAttempts);
        this.reconnectAttempts += 1;

        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = setTimeout(() => this.#open(), delay);
    }

    sendAudio(buffer) {
        if (this.socket?.readyState === WebSocket.OPEN) {
            this.socket.send(buffer);
        }
    }

    updateConfig(config) {
        this.pendingConfig = { ...this.pendingConfig, ...config };
        if (this.socket?.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify({ type: "config", ...this.pendingConfig }));
        }
    }

    close() {
        this.shouldReconnect = false;
        clearTimeout(this.reconnectTimer);
        if (this.socket) {
            this.socket.close();
        }
    }
}
